import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Film, Upload, Trash2, Loader2, AlertCircle, CheckCircle2 } from "lucide-react";

const FRAME_COUNT = 48;
const MAX_VIDEO_BYTES = 200 * 1024 * 1024; // 200 MB
const ALLOWED_VIDEO_TYPES = ["video/mp4", "video/webm", "video/quicktime"];
const FRAME_WIDTH = 1280;
const BATCH_SIZE = 5;

interface VideoFrameExtractorProps {
  frameCount: number;
  onFramesUploaded: (count: number) => Promise<void>;
  onDelete: () => Promise<void>;
  uploadFrame: (blob: Blob, index: number) => Promise<void>;
}

type Status = "idle" | "extracting" | "uploading" | "done" | "error";

async function extractFrames(
  file: File,
  count: number,
  onProgress: (i: number) => void
): Promise<Blob[]> {
  return new Promise((resolve, reject) => {
    const video = document.createElement("video");
    video.muted = true;
    video.preload = "auto";
    const url = URL.createObjectURL(file);
    video.src = url;

    video.addEventListener("error", () => {
      URL.revokeObjectURL(url);
      reject(new Error("Video could not be loaded"));
    });

    video.addEventListener("loadedmetadata", async () => {
      const scale = Math.min(1, FRAME_WIDTH / video.videoWidth);
      const w = Math.round(video.videoWidth * scale);
      const h = Math.round(video.videoHeight * scale);

      const canvas = document.createElement("canvas");
      canvas.width = w;
      canvas.height = h;
      const ctx = canvas.getContext("2d")!;
      const frames: Blob[] = [];

      for (let i = 0; i < count; i++) {
        video.currentTime = (video.duration / (count - 1)) * i;
        await new Promise<void>((res) =>
          video.addEventListener("seeked", () => res(), { once: true })
        );
        ctx.drawImage(video, 0, 0, w, h);
        const blob = await new Promise<Blob>((res) =>
          canvas.toBlob((b) => res(b!), "image/jpeg", 0.82)
        );
        frames.push(blob);
        onProgress(i + 1);
      }

      URL.revokeObjectURL(url);
      resolve(frames);
    });

    video.load();
  });
}

async function uploadInBatches(
  frames: Blob[],
  uploadFn: (blob: Blob, index: number) => Promise<void>,
  onProgress: (done: number) => void
) {
  let done = 0;
  for (let i = 0; i < frames.length; i += BATCH_SIZE) {
    const batch = frames.slice(i, i + BATCH_SIZE);
    await Promise.all(
      batch.map(async (blob, j) => {
        await uploadFn(blob, i + j);
        done++;
        onProgress(done);
      })
    );
  }
}

export function VideoFrameExtractor({
  frameCount,
  onFramesUploaded,
  onDelete,
  uploadFrame,
}: VideoFrameExtractorProps) {
  const { t: tRaw } = useTranslation();
  const t = (key: string, vars?: Record<string, unknown>) => tRaw(`themes.${key}`, vars);

  const inputRef = useRef<HTMLInputElement>(null);
  const [status, setStatus] = useState<Status>("idle");
  const [extractDone, setExtractDone] = useState(0);
  const [uploadDone, setUploadDone] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);

  function validateVideo(file: File): string | null {
    if (!ALLOWED_VIDEO_TYPES.includes(file.type)) return t("videoInvalidType");
    if (file.size > MAX_VIDEO_BYTES) return t("videoTooLarge");
    return null;
  }

  async function handleVideoChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = "";
    setError(null);

    const err = validateVideo(file);
    if (err) { setError(err); return; }

    try {
      // 1. Extract frames
      setStatus("extracting");
      setExtractDone(0);
      const frames = await extractFrames(file, FRAME_COUNT, (i) => setExtractDone(i));

      // 2. Upload frames in batches
      setStatus("uploading");
      setUploadDone(0);
      await uploadInBatches(frames, uploadFrame, (done) => setUploadDone(done));

      // 3. Commit frame count
      await onFramesUploaded(FRAME_COUNT);
      setStatus("done");
    } catch (e) {
      console.error("Frame extraction failed:", e);
      setError(t("videoError"));
      setStatus("error");
    }
  }

  async function handleDelete() {
    if (!window.confirm(tRaw("themes.deleteVideoConfirm"))) return;
    setDeleting(true);
    try {
      await onDelete();
      setStatus("idle");
      setError(null);
    } finally {
      setDeleting(false);
    }
  }

  const hasFrames = frameCount > 0;
  const isProcessing = status === "extracting" || status === "uploading";

  return (
    <div className="rounded-2xl border border-border overflow-hidden">
      {/* Header */}
      <div className="flex items-start gap-3 bg-muted/50 px-4 py-4 border-b border-border">
        <div className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-xl bg-primary/10">
          <Film className="h-4 w-4 text-primary" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center justify-between">
            <p className="text-sm font-semibold">{t("videoSection")}</p>
            {hasFrames && !isProcessing && (
              <span className="flex items-center gap-1 text-xs text-emerald-600 dark:text-emerald-400 font-medium">
                <CheckCircle2 className="h-3.5 w-3.5" />
                {t("videoFrameCount", { count: frameCount })}
              </span>
            )}
          </div>
          <p className="mt-0.5 text-xs text-muted-foreground leading-relaxed">
            {t("videoDesc")}
          </p>
        </div>
      </div>

      <div className="p-4 space-y-3">
        <input
          ref={inputRef}
          type="file"
          accept="video/mp4,video/webm,video/quicktime"
          className="hidden"
          onChange={handleVideoChange}
          disabled={isProcessing}
        />

        {/* Processing progress */}
        {isProcessing && (
          <div className="space-y-3">
            <div className="flex items-center justify-between text-sm">
              <span className="text-muted-foreground flex items-center gap-2">
                <Loader2 className="h-4 w-4 animate-spin" />
                {status === "extracting" ? t("videoExtracting") : t("videoUploading")}
              </span>
              <span className="tabular-nums text-xs font-medium">
                {t("videoProgress", {
                  done: status === "extracting" ? extractDone : uploadDone,
                  total: FRAME_COUNT,
                })}
              </span>
            </div>
            <div className="h-2 rounded-full bg-muted overflow-hidden">
              <div
                className="h-full bg-primary rounded-full transition-all duration-300"
                style={{
                  width: `${((status === "extracting" ? extractDone : uploadDone) / FRAME_COUNT) * 100}%`,
                }}
              />
            </div>
          </div>
        )}

        {/* Actions */}
        {!isProcessing && (
          <div className="flex gap-2">
            <button
              onClick={() => inputRef.current?.click()}
              className="flex flex-1 items-center justify-center gap-1.5 rounded-xl border border-border py-2 text-sm font-medium hover:bg-muted transition-colors cursor-pointer disabled:opacity-50"
            >
              <Upload className="h-4 w-4" />
              {hasFrames ? t("change") : t("videoUpload")}
            </button>
            {hasFrames && (
              <button
                onClick={handleDelete}
                disabled={deleting}
                className="flex items-center gap-1.5 rounded-xl border border-red-200 dark:border-red-900 bg-red-50 dark:bg-red-950/30 px-4 py-2 text-sm font-medium text-red-600 dark:text-red-400 hover:bg-red-100 dark:hover:bg-red-950/50 transition-colors cursor-pointer disabled:opacity-50"
              >
                {deleting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
                {t("videoRemove")}
              </button>
            )}
          </div>
        )}

        {/* Error */}
        {error && (
          <div className="flex items-start gap-2 rounded-xl bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-900 px-3 py-2.5 text-xs text-red-700 dark:text-red-400">
            <AlertCircle className="h-3.5 w-3.5 shrink-0 mt-0.5" />
            <span>{error}</span>
          </div>
        )}
      </div>
    </div>
  );
}
